import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection, doc, onSnapshot, orderBy, query, runTransaction, setDoc,
  serverTimestamp, updateDoc, writeBatch, increment, deleteField,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../components/UserContext';
import { useClients, useContracts, useOrdenesTrabajoRaw } from '../contexts/DataContext';
import { useOrgMiembros } from './useOrgMiembros';
import { logBitacora } from '../utils/bitacora';
import {
  resolverConfigOt,
  fasesDe,
  nombreTipo,
  estadoFase,
  buildOt,
  buildCambioFase,
  buildAsignacionOt,
  buildEquiposOt,
  buildCierre,
  buildReapertura,
  buildMensaje,
  buildNotificaciones,
  buildActividad,
  destinatariosFase,
  destinatariosMensaje,
  buildFaseTerminada,
  destinatariosFaseTerminada,
  extraerMenciones,
  buildAprobacion,
  destinatariosAprobacion,
  APROBACION_RESULTADOS,
  faseDe,
  equipoDe,
  folioTexto,
  isAbierta,
  NOTIF_TIPOS,
  OT_RESULTADO_LABELS,
  TITULO_MAX_LEN,
  DESCRIPCION_MAX_LEN,
} from '../utils/ordenesTrabajo';

// Module-level cache so every surface shares one config listener per org.
const configCache = new Map(); // org -> { data, subs:Set<fn>, unsub }

function suscribirConfig(org, cb) {
  let entry = configCache.get(org);
  if (!entry) {
    entry = { data: undefined, subs: new Set(), unsub: null };
    configCache.set(org, entry);
    entry.unsub = onSnapshot(
      doc(db, 'orgs', org, 'config', 'ot'),
      (snap) => {
        entry.data = snap.exists() ? snap.data() : null;
        entry.subs.forEach((fn) => fn(entry.data));
      },
      (e) => {
        console.error('config/ot listener:', e);
        entry.data = null;
        entry.subs.forEach((fn) => fn(null));
      }
    );
  }
  entry.subs.add(cb);
  if (entry.data !== undefined) cb(entry.data);
  return () => {
    entry.subs.delete(cb);
    if (entry.subs.size === 0) {
      entry.unsub?.();
      configCache.delete(org);
    }
  };
}

/**
 * The org's OT config (equipos + fases), live, with defaults when nothing is
 * stored yet. `stored` is the raw doc (undefined while loading, null when
 * absent) for the config screen; everyone else reads `config`.
 *
 * @return {{config:{equipos:Array, flujos:Array}, stored:Object|null|undefined, loading:boolean, guardar:Function}}
 */
export function useOtConfig() {
  const { userData } = useUser();
  const org = userData?.org || null;
  const [stored, setStored] = useState(() => (org && configCache.get(org)?.data) ?? undefined);

  useEffect(() => {
    if (!org) return undefined;
    return suscribirConfig(org, setStored);
  }, [org]);

  const config = useMemo(() => resolverConfigOt(stored), [stored]);

  // Owner-only by rules (orgs/{org}/config/* is owner-write).
  const guardar = useCallback(async (next) => {
    if (!org) throw new Error('Sin organización');
    await setDoc(doc(db, 'orgs', org, 'config', 'ot'), {
      equipos: next.equipos,
      flujos: next.flujos,
      coordinadores: Array.isArray(next.coordinadores) ? next.coordinadores : [],
      aprobadores: Array.isArray(next.aprobadores) ? next.aprobadores : [],
      // Legacy root key from the first build; cleared so it cannot shadow.
      fases: deleteField(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [org]);

  return { config, stored, loading: stored === undefined, guardar };
}

/**
 * The org's OTs decorated with the names the lists render.
 *
 * @return {{rows:Array<Object>|null, abiertas:Array<Object>, loading:boolean, otEnabled:boolean, config:Object}}
 */
export function useOrdenesTrabajo() {
  const { otEnabled, ordenesTrabajo, ordenesTrabajoLoading } = useOrdenesTrabajoRaw();
  const { clientsById } = useClients();
  const { contractsById } = useContracts();
  const { config, loading: configLoading } = useOtConfig();

  const rows = useMemo(() => {
    if (ordenesTrabajoLoading) return null;
    return ordenesTrabajo.map((ot) => {
      const cliente = clientsById[ot.clienteId] || {};
      const contrato = contractsById[ot.contratoId] || {};
      const fianza = (contrato.fianzas || []).find((f) => f.id === ot.fianzaId) || null;
      const fase = faseDe(fasesDe(config, ot.tipo), ot.faseId);
      return {
        ...ot,
        clientName: cliente.nombreEmpresa || '',
        contractTitle: contrato.tituloContrato?.value || '',
        contractNumber: contrato.numeroContrato?.value || '',
        // When the contrato was uploaded (Fran: "necesito ver la fecha en la
        // que se subió un contrato"). Timestamp on most docs, ms on some.
        contractCreatedAt: contrato.createdAt?.toMillis
          ? contrato.createdAt.toMillis()
          : (contrato.createdAt?.seconds ? contrato.createdAt.seconds * 1000 : Number(contrato.createdAt) || null),
        fianzaNumero: fianza?.numeroFianza || '',
        tipoNombre: nombreTipo(config, ot.tipo),
        faseNombre: fase?.nombre || '',
        faseObjetivo: fase?.diasObjetivo || null,
        faseAprobacion: Boolean(fase?.requiereAprobacion),
        faseEstado: estadoFase(ot, fase),
      };
    });
  }, [ordenesTrabajo, ordenesTrabajoLoading, clientsById, contractsById, config]);

  const abiertas = useMemo(() => (rows || []).filter(isAbierta), [rows]);

  return { rows, abiertas, loading: ordenesTrabajoLoading || configLoading, otEnabled, config };
}

/**
 * The thread of one OT: messages and system events, oldest first. Own
 * listener per open OT — only the OtView mounts it, one at a time.
 *
 * @param {string|null} otId
 * @return {{mensajes:Array<Object>, loading:boolean}}
 */
export function useOtMensajes(otId) {
  const [mensajes, setMensajes] = useState([]);
  const [loading, setLoading] = useState(Boolean(otId));

  useEffect(() => {
    if (!otId) { setMensajes([]); setLoading(false); return undefined; }
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'ordenes_trabajo', otId, 'mensajes'), orderBy('at', 'asc')),
      (snap) => {
        setMensajes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (e) => { console.error('ot mensajes listener:', e); setLoading(false); }
    );
    return () => unsub();
  }, [otId]);

  return { mensajes, loading };
}

/**
 * Every write on an OT, in one place, so each one stamps the same things: the
 * OT update, the system event in its thread, the notificaciones for whoever
 * should hear about it, and the bitácora entry.
 *
 * Bitácora hangs off the cliente when the OT has one, else the org
 * (`ordenes_trabajo` is not in the rules' collection enum, like pendientes).
 */
export function useOtAcciones() {
  const { user, userData } = useUser();
  const { config } = useOtConfig();
  const { nombreDe, miembros } = useOrgMiembros();
  const org = userData?.org || null;
  const yo = useMemo(
    () => ({ uid: user?.uid || null, nombre: userData?.nombre || userData?.correo || null }),
    [user?.uid, userData?.nombre, userData?.correo]
  );

  const bitacora = useCallback(async (ot, action, description, extra = {}) => {
    try {
      await logBitacora({
        action,
        collection: ot?.clienteId ? 'clientes' : 'orgs',
        documentId: ot?.clienteId || org,
        user,
        userData,
        metadata: {
          description,
          clientId: ot?.clienteId || null,
          contratoId: ot?.contratoId || null,
          otId: ot?.id || null,
          otFolio: ot?.folio ?? null,
          ...extra,
        },
      });
    } catch (e) {
      console.error('bitacora OT failed', e);
    }
  }, [org, user, userData]);

  const notificar = useCallback((batch, rows) => {
    rows.forEach((n) => batch.set(doc(collection(db, 'notificaciones')), n));
  }, []);

  const evento = useCallback((batch, otId, evento, datos, ts) => {
    batch.set(
      doc(collection(db, 'ordenes_trabajo', otId, 'mensajes')),
      buildMensaje({ tipo: 'evento', evento, datos, autor: yo, ts })
    );
  }, [yo]);

  /**
   * Create an OT with the next folio of the org (transaction on
   * orgs/{org}/contadores/ot), then its "creada" event and the notificaciones
   * for the asignados.
   */
  const crear = useCallback(async (campos) => {
    if (!org) throw new Error('Sin organización');
    const ts = Date.now();
    const contadorRef = doc(db, 'orgs', org, 'contadores', 'ot');
    const otRef = doc(collection(db, 'ordenes_trabajo'));
    const data = await runTransaction(db, async (tx) => {
      const snap = await tx.get(contadorRef);
      const siguiente = snap.exists() ? Number(snap.data().siguiente || 1) : 1;
      tx.set(contadorRef, { siguiente: siguiente + 1, updatedAt: ts }, { merge: true });
      const ot = buildOt({ ...campos, org, folio: siguiente, createdBy: yo, ts });
      tx.set(otRef, ot);
      return ot;
    });
    const ot = { id: otRef.id, ...data };

    const fasesTipo = fasesDe(config, ot.tipo);
    const batch = writeBatch(db);
    evento(batch, ot.id, 'creada', {
      faseId: ot.faseId, faseNombre: faseDe(fasesTipo, ot.faseId)?.nombre || null,
    }, ts);
    const otros = ot.asignados.filter((u) => u !== yo.uid);
    if (otros.length) {
      notificar(batch, buildNotificaciones({
        org, para: otros, tipo: NOTIF_TIPOS.ASIGNACION, ot,
        texto: `${yo.nombre || 'Alguien'} te asignó la ${folioTexto(ot.folio)}`, por: yo, ts,
      }));
    }
    const fase = faseDe(fasesTipo, ot.faseId);
    const equipoFase = fase?.equipoId ? equipoDe(config.equipos, fase.equipoId) : null;
    if (equipoFase?.miembros?.length) {
      notificar(batch, buildNotificaciones({
        org, para: equipoFase.miembros.filter((u) => !ot.asignados.includes(u)), tipo: NOTIF_TIPOS.FASE, ot,
        texto: `${folioTexto(ot.folio)} entró a la fase ${fase.nombre} (${equipoFase.nombre})`, por: yo, ts,
      }));
    }
    if (fase?.requiereAprobacion) {
      notificar(batch, buildNotificaciones({
        org, para: destinatariosAprobacion(config), tipo: NOTIF_TIPOS.APROBACION, ot,
        texto: `${folioTexto(ot.folio)} espera aprobación en ${fase.nombre}`, por: yo, ts,
      }));
    }
    await batch.commit();
    await bitacora(ot, 'CREATE', `Abrió ${folioTexto(ot.folio)}: ${ot.titulo}`);
    return ot;
  }, [org, yo, config, evento, notificar, bitacora]);

  /**
   * Relevo: the OT passes to `faseId`. With `asignados`, the coordinador also
   * hands it to the people who work the new fase (they replace the current
   * ones); with `nota`, the hilo carries why.
   */
  const moverFase = useCallback(async (ot, faseId, { asignados = null, nota = '' } = {}) => {
    if (!ot?.id || (ot.faseId === faseId && !Array.isArray(asignados))) return;
    const ts = Date.now();
    const fasesTipo = fasesDe(config, ot.tipo);
    const de = faseDe(fasesTipo, ot.faseId);
    const a = faseDe(fasesTipo, faseId);
    const nuevos = Array.isArray(asignados) ? asignados : null;
    const nombres = nuevos ? nuevos.map((u) => nombreDe(u) || u).join(', ') : null;
    const limpia = String(nota || '').trim().slice(0, 300) || null;
    const batch = writeBatch(db);
    batch.update(doc(db, 'ordenes_trabajo', ot.id), buildCambioFase(ot, faseId, yo, a?.nombre || faseId, ts, nuevos));
    evento(batch, ot.id, 'fase', {
      de: ot.faseId || null, deNombre: de?.nombre || null, a: faseId, aNombre: a?.nombre || null,
      asignados: nuevos, nombres, nota: limpia,
    }, ts);
    const equipoA = a?.equipoId ? equipoDe(config.equipos, a.equipoId) : null;
    const despues = { ...ot, asignados: nuevos || ot.asignados };
    if (nuevos) {
      const antes = Array.isArray(ot.asignados) ? ot.asignados : [];
      const recien = nuevos.filter((u) => !antes.includes(u));
      if (recien.length) {
        notificar(batch, buildNotificaciones({
          org, para: recien, tipo: NOTIF_TIPOS.ASIGNACION, ot,
          texto: `${yo.nombre || 'Alguien'} te asignó la ${folioTexto(ot.folio)} en ${a?.nombre || faseId}`, por: yo, ts,
        }));
      }
    }
    notificar(batch, buildNotificaciones({
      org, para: destinatariosFase(despues, a, config.equipos).filter((u) => !(nuevos || []).includes(u) || (ot.asignados || []).includes(u)),
      tipo: NOTIF_TIPOS.FASE, ot,
      texto: `${folioTexto(ot.folio)} avanzó a ${a?.nombre || faseId}${equipoA ? ` (${equipoA.nombre})` : ''}`,
      por: yo, ts,
    }));
    if (a?.requiereAprobacion) {
      notificar(batch, buildNotificaciones({
        org, para: destinatariosAprobacion(config), tipo: NOTIF_TIPOS.APROBACION, ot,
        texto: `${folioTexto(ot.folio)} espera aprobación en ${a.nombre}`, por: yo, ts,
      }));
    }
    await batch.commit();
    await bitacora(ot, 'UPDATE', `${folioTexto(ot.folio)} a ${a?.nombre || faseId}${nombres ? `, asignada a ${nombres}` : ''}`);
  }, [org, yo, config, nombreDe, evento, notificar, bitacora]);

  /**
   * Approval fase resolved. Approving is a relevo forward (fase + asignados)
   * with the approval recorded; rejecting sends the OT back (default: the
   * previous fase, to the people who worked it) with a motivo. Both stay in
   * `aprobaciones[]` and in the hilo; the rules only let aprobadores write
   * that field.
   */
  const resolverAprobacion = useCallback(async (ot, { resultado, motivo = '', faseId, asignados = null, nota = '' }) => {
    if (!ot?.id || !faseId) return;
    const ts = Date.now();
    const fasesTipo = fasesDe(config, ot.tipo);
    const de = faseDe(fasesTipo, ot.faseId);
    const a = faseDe(fasesTipo, faseId);
    const aprobada = resultado === APROBACION_RESULTADOS.APROBADA;
    const nuevos = Array.isArray(asignados) ? asignados : null;
    const nombres = nuevos ? nuevos.map((u) => nombreDe(u) || u).join(', ') : null;
    const limpioMotivo = String(motivo || '').trim().slice(0, 300) || null;
    const limpiaNota = String(nota || '').trim().slice(0, 300) || null;
    const batch = writeBatch(db);
    batch.update(doc(db, 'ordenes_trabajo', ot.id), {
      ...buildCambioFase(ot, faseId, yo, a?.nombre || faseId, ts, nuevos),
      ...buildAprobacion(ot, { resultado, motivo: aprobada ? nota : motivo, quien: yo, faseNombre: de?.nombre || '', ts }),
    });
    evento(batch, ot.id, 'aprobacion', {
      resultado, motivo: aprobada ? limpiaNota : limpioMotivo, faseId: ot.faseId || null, faseNombre: de?.nombre || null,
      a: faseId, aNombre: a?.nombre || null, asignados: nuevos, nombres,
    }, ts);
    const equipoA = a?.equipoId ? equipoDe(config.equipos, a.equipoId) : null;
    const despues = { ...ot, asignados: nuevos || ot.asignados };
    const texto = aprobada
      ? `${folioTexto(ot.folio)} aprobada, avanzó a ${a?.nombre || faseId}`
      : `${folioTexto(ot.folio)} rechazada en ${de?.nombre || 'aprobación'}${limpioMotivo ? `: ${limpioMotivo}` : ''}`;
    notificar(batch, buildNotificaciones({
      org, para: [...(nuevos || []), ...destinatariosFase(despues, a, config.equipos), ...(ot.asignados || [])],
      tipo: NOTIF_TIPOS.APROBACION, ot, texto, por: yo, ts,
    }));
    if (a?.requiereAprobacion) {
      notificar(batch, buildNotificaciones({
        org, para: destinatariosAprobacion(config), tipo: NOTIF_TIPOS.APROBACION, ot,
        texto: `${folioTexto(ot.folio)} espera aprobación en ${a.nombre}`, por: yo, ts,
      }));
    }
    await batch.commit();
    await bitacora(ot, 'UPDATE', `${folioTexto(ot.folio)} ${aprobada ? 'aprobada' : 'rechazada'} en ${de?.nombre || 'aprobación'}${!aprobada && limpioMotivo ? `: ${limpioMotivo}` : ''}${equipoA ? ` (${equipoA.nombre})` : ''}`);
  }, [org, yo, config, nombreDe, evento, notificar, bitacora]);

  /**
   * Whoever works the fase declares it done. The OT stays in its fase,
   * flagged; the coordinadores hear about it and run the relevo.
   */
  const terminarFase = useCallback(async (ot, nota = '') => {
    if (!ot?.id || ot.faseTerminada?.at) return;
    const ts = Date.now();
    const fase = faseDe(fasesDe(config, ot.tipo), ot.faseId);
    const payload = buildFaseTerminada(yo, nota, ts);
    const batch = writeBatch(db);
    batch.update(doc(db, 'ordenes_trabajo', ot.id), payload);
    evento(batch, ot.id, 'faseTerminada', { faseId: ot.faseId || null, faseNombre: fase?.nombre || null, nota: payload.faseTerminada.nota }, ts);
    notificar(batch, buildNotificaciones({
      org, para: destinatariosFaseTerminada(config), tipo: NOTIF_TIPOS.FASE_TERMINADA, ot,
      texto: `${folioTexto(ot.folio)}: ${fase?.nombre || 'fase'} terminada, por asignar`, por: yo, ts,
    }));
    await batch.commit();
    await bitacora(ot, 'UPDATE', `${folioTexto(ot.folio)}: terminó ${fase?.nombre || 'la fase'}`);
  }, [org, yo, config, evento, notificar, bitacora]);

  const asignar = useCallback(async (ot, uids) => {
    if (!ot?.id) return;
    const ts = Date.now();
    const antes = Array.isArray(ot.asignados) ? ot.asignados : [];
    const nuevos = uids.filter((u) => !antes.includes(u));
    const nombres = uids.map((u) => nombreDe(u) || u).join(', ');
    const batch = writeBatch(db);
    batch.update(doc(db, 'ordenes_trabajo', ot.id), buildAsignacionOt(uids, yo, nombres, ts));
    evento(batch, ot.id, 'asignacion', { asignados: uids, nombres }, ts);
    if (nuevos.length) {
      notificar(batch, buildNotificaciones({
        org, para: nuevos, tipo: NOTIF_TIPOS.ASIGNACION, ot,
        texto: `${yo.nombre || 'Alguien'} te asignó la ${folioTexto(ot.folio)}`, por: yo, ts,
      }));
    }
    await batch.commit();
    await bitacora(ot, 'UPDATE', `${folioTexto(ot.folio)} asignada a ${nombres || 'nadie'}`);
  }, [org, yo, nombreDe, evento, notificar, bitacora]);

  const setEquipos = useCallback(async (ot, equipoIds) => {
    if (!ot?.id) return;
    const ts = Date.now();
    const antes = Array.isArray(ot.equipos) ? ot.equipos : [];
    const nuevos = equipoIds.filter((e) => !antes.includes(e));
    const nombres = equipoIds.map((e) => equipoDe(config.equipos, e)?.nombre || e).join(', ');
    const batch = writeBatch(db);
    batch.update(doc(db, 'ordenes_trabajo', ot.id), buildEquiposOt(equipoIds, yo, nombres, ts));
    evento(batch, ot.id, 'equipos', { equipos: equipoIds, nombres }, ts);
    const para = nuevos.flatMap((e) => equipoDe(config.equipos, e)?.miembros || []);
    if (para.length) {
      notificar(batch, buildNotificaciones({
        org, para, tipo: NOTIF_TIPOS.ASIGNACION, ot,
        texto: `${folioTexto(ot.folio)} se asignó a tu equipo`, por: yo, ts,
      }));
    }
    await batch.commit();
    await bitacora(ot, 'UPDATE', `${folioTexto(ot.folio)} equipos: ${nombres || 'ninguno'}`);
  }, [org, yo, config, evento, notificar, bitacora]);

  const cerrar = useCallback(async (ot, { resultado, motivo }) => {
    if (!ot?.id) return;
    const ts = Date.now();
    const batch = writeBatch(db);
    batch.update(doc(db, 'ordenes_trabajo', ot.id), buildCierre(ot, { resultado, motivo, user, nombre: yo.nombre, ts }));
    evento(batch, ot.id, 'cierre', { resultado, motivo: motivo || null }, ts);
    notificar(batch, buildNotificaciones({
      org, para: ot.asignados || [], tipo: NOTIF_TIPOS.CIERRE, ot,
      texto: `${folioTexto(ot.folio)} se cerró: ${OT_RESULTADO_LABELS[resultado] || resultado}`, por: yo, ts,
    }));
    await batch.commit();
    await bitacora(ot, 'UPDATE', `Cerró ${folioTexto(ot.folio)} (${OT_RESULTADO_LABELS[resultado] || resultado})${motivo ? `: ${motivo}` : ''}`);
  }, [org, yo, user, evento, notificar, bitacora]);

  const reabrir = useCallback(async (ot, motivo) => {
    if (!ot?.id) return;
    const ts = Date.now();
    const batch = writeBatch(db);
    batch.update(doc(db, 'ordenes_trabajo', ot.id), buildReapertura(ot, { motivo, user, nombre: yo.nombre, ts }));
    evento(batch, ot.id, 'reapertura', { motivo: motivo || null }, ts);
    notificar(batch, buildNotificaciones({
      org, para: ot.asignados || [], tipo: NOTIF_TIPOS.CIERRE, ot,
      texto: `${folioTexto(ot.folio)} se reabrió`, por: yo, ts,
    }));
    await batch.commit();
    await bitacora(ot, 'UPDATE', `Reabrió ${folioTexto(ot.folio)}${motivo ? `: ${motivo}` : ''}`);
  }, [org, yo, user, evento, notificar, bitacora]);

  /**
   * A message in the hilo. @"Nombre" tags anyone in the org: they get a
   * `mencion` notification whether or not they follow the OT; the rest of the
   * thread gets the usual `mensaje` one.
   */
  const enviarMensaje = useCallback(async (ot, texto, mensajesPrevios = []) => {
    const limpio = String(texto || '').trim();
    if (!ot?.id || !limpio) return;
    const ts = Date.now();
    const menciones = extraerMenciones(limpio, miembros);
    const batch = writeBatch(db);
    batch.set(doc(collection(db, 'ordenes_trabajo', ot.id, 'mensajes')), buildMensaje({ tipo: 'mensaje', texto: limpio, autor: yo, menciones, ts }));
    batch.update(doc(db, 'ordenes_trabajo', ot.id), {
      mensajes: increment(1),
      updatedAt: ts,
      ...buildActividad('mensaje', yo, limpio, ts),
    });
    if (menciones.length) {
      notificar(batch, buildNotificaciones({
        org, para: menciones, tipo: NOTIF_TIPOS.MENCION, ot,
        texto: `${yo.nombre || 'Alguien'} te mencionó en ${folioTexto(ot.folio)}: ${limpio.slice(0, 120)}`, por: yo, ts,
      }));
    }
    notificar(batch, buildNotificaciones({
      org, para: destinatariosMensaje(ot, mensajesPrevios).filter((u) => !menciones.includes(u)), tipo: NOTIF_TIPOS.MENSAJE, ot,
      texto: `${yo.nombre || 'Alguien'} en ${folioTexto(ot.folio)}: ${limpio.slice(0, 120)}`, por: yo, ts,
    }));
    await batch.commit();
  }, [org, yo, miembros, notificar]);

  /** Title, description, fecha compromiso and the relation (cliente/contrato/fianza). */
  const editar = useCallback(async (ot, campos) => {
    if (!ot?.id) return;
    const ts = Date.now();
    const cambios = {};
    if (campos.titulo !== undefined) cambios.titulo = String(campos.titulo).trim().slice(0, TITULO_MAX_LEN);
    if (campos.descripcion !== undefined) cambios.descripcion = String(campos.descripcion || '').trim().slice(0, DESCRIPCION_MAX_LEN) || null;
    if (campos.fechaCompromiso !== undefined) cambios.fechaCompromiso = campos.fechaCompromiso ? Number(campos.fechaCompromiso) : null;
    if (campos.tipo !== undefined) cambios.tipo = campos.tipo;
    ['clienteId', 'contratoId', 'fianzaId', 'afianzadora'].forEach((k) => {
      if (campos[k] !== undefined) cambios[k] = campos[k] || null;
    });
    const relacionCambio = ['clienteId', 'contratoId', 'fianzaId'].some((k) => campos[k] !== undefined && (campos[k] || null) !== (ot[k] || null));
    const batch = writeBatch(db);
    batch.update(doc(db, 'ordenes_trabajo', ot.id), {
      ...cambios, updatedAt: ts,
      ...buildActividad(relacionCambio ? 'relacion' : 'editada', yo, cambios.titulo || ot.titulo, ts),
    });
    if (relacionCambio) {
      evento(batch, ot.id, 'relacion', {
        clienteId: cambios.clienteId ?? ot.clienteId ?? null,
        contratoId: cambios.contratoId ?? ot.contratoId ?? null,
        fianzaId: cambios.fianzaId ?? ot.fianzaId ?? null,
      }, ts);
    }
    await batch.commit();
    await bitacora({ ...ot, ...cambios }, 'UPDATE', `Editó ${folioTexto(ot.folio)}: ${cambios.titulo || ot.titulo}`);
  }, [yo, evento, bitacora]);

  return {
    crear, moverFase, terminarFase, resolverAprobacion, asignar, setEquipos, cerrar, reabrir, enviarMensaje, editar,
    miembros, nombreDe, config, yo,
  };
}

/**
 * Mark one or every notification read. Kept here so the bell stays a view.
 */
export async function marcarNotificacionLeida(id) {
  await updateDoc(doc(db, 'notificaciones', id), { leida: true, leidaAt: Date.now() });
}

export async function marcarNotificacionesLeidas(ids) {
  if (!ids.length) return;
  const batch = writeBatch(db);
  const ts = Date.now();
  ids.slice(0, 450).forEach((id) => batch.update(doc(db, 'notificaciones', id), { leida: true, leidaAt: ts }));
  await batch.commit();
}
